/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI Cloud (C) Copyright IBM Corp. 2020, 2021
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'../lib/@waca/dashboard-common/dist/core/APIFactory',
	'../dashboard/loader/BoardLoaderHelper',
	'../api/impl/LegacyViewControllers',
	'../api/impl/DashboardCore',
	'../app/util/GlassUtil',
	'./features/contentStore/ContentStore',
	'./features/userProfile/CAUserProfile'
], function (
	APIFactory,
	BoardLoaderHelper,
	LegacyViewControllers,
	DashboardCore,
	GlassUtil,
	ContentStore,
	CAUserProfile
) {

	const DEFAULT_FEATURES_EXCLUDES = {
		'com.ibm.bi.dashboard.features': [
			'DashboardLearning',
			'deleteAction',
			'groupAction',
			'duplicateAction',
			'orderAction',
			'alignAction',
			'pinAction',
			'launchIntoExplore',
			'disconnectEventGroupAction',
			'linkEventGroupAction',
			'createEventGroupAction',
			'ResetDashboard',
			'editNotebookAction',
			'ConvertToTemplate',
			'CanvasDnD',
			'InAppSlideoutState',
			'InAppSlideoutDOM',
			'PropertiesPane',
			'PropertiesPaneDOM',
			'PropertiesAction',
			'lassoSelectAction',
			'contextualGridAction',
			'jumpToAction',
			'changeVisTypeAction',
			'suppressionAction',
			'HideRowColumnAction',
			'SearchAction',
			'AggregationTypeAction',
			'FormatAction',
			'CrosstabActions',
			'FilterDockAction',
			'MetadataDnDProvider',
			'SlotEditorState',
			'SlotEditorDOM',
			'OnLoadFilterModifier',
			'ToolbarDock',
			'ToolbarDockDOM',
			'OnDemandToolbarSectionDOM',
			'CanvasToolbarSectionDOM',
			'Print',
			'DashboardPrint',
			'developerWidgetRefreshAction',
			'LocalFilterExtension',
			'SampleFeature',
			'CsvExport',
			'ToolbarPanelDockDOM',
			'PropertiesPaneDOMV2'
		],
		'com.ibm.bi.dashboard.content-features': [
			'VisDnD',
			'VisDnD.utils',
			'contextualGridAction',
			'Visualization.SmartsRecommender',
			'Toolbar',
			'HideRowColumnAction',
			'VisExpandMode',
			'DataBehindTheVis',
			'ViprUITest',
			'LocalFilterDialog',
			'ContentMenu',
			'DropZonesOverlayState',
			'DropZonesOverlayDOM'
		]
	};

	/**
	 * @classdesc API class that is used to create a custom dashboard application that is dependent on glass.
	 * Glass refers to an internal Cognos Analytics API that controls the user interface.  It allows developers to perform actions on the UI,
	 * such as opening a view or accessing view configurations or services.
	 */
	class AppFactory {
		/**
		 * @param {Object} options
		 * @param {String} options.glassContext - Contains the glass context of the application.
		 * @param {String[]} options.features.name.excludes - List of feature names to prevent from loading for a feature collection.
		 * @param {String[]} options.apiConsumerConfig - Object used to modify api consumer flags, e.g  { metadataBlocksVisualization : false }
		 *
		 */
		constructor(options = {}) {
			this._glassContext = options.glassContext || window.__glassAppController.glassContext;
			const dashboardFeatures = options.features && options.features['com.ibm.bi.dashboard.features'] || {};
			const contentFeatures = options.features && options.features['com.ibm.bi.dashboard.content-features'] || {};
			this._featuresOverride = {
				'com.ibm.bi.dashboard.features': {
					excludes: DEFAULT_FEATURES_EXCLUDES['com.ibm.bi.dashboard.features'].concat(dashboardFeatures.excludes || [])
				},
				'com.ibm.bi.dashboard.content-features': {
					excludes: DEFAULT_FEATURES_EXCLUDES['com.ibm.bi.dashboard.content-features'].concat(contentFeatures.excludes || [])
				},
				'bootstrap': options.features && options.features.bootstrap
			};
			const apiConsumerConfigDefaults = {
				metadataBlocksVisualization: true,
				disableQueryPreExecution: true
			};
			this._apiConsumerConfig = Object.assign(apiConsumerConfigDefaults, options.apiConsumerConfig);
			const _ajaxService = GlassUtil.getAjaxService(this._glassContext);
			const contentStore = new ContentStore(_ajaxService);
			const userProfile = new CAUserProfile();
			userProfile.initialize(this._glassContext);
			this._featureMap = new Map();
			this._featureMap.set('ContentStore', contentStore.getAPI());
			this._featureMap.set('UserProfile', userProfile.getAPI());

			return APIFactory.createAPI(this, [
				class {
					create(){}
					getFeature(){}
				}
			]);
		}

		/**
		 *
		 * @param {Object} options
		 * @param {String} options.assetId - Specifies the dashboard asset ID.
		 * @param {String} options.spec - Specifies the dashboard specification.
		 * @param {String[]} options.features.name.excludes - List of feature names to prevent from loading for a feature collection.
		 * @return {Promise<DashboardCoreAPI>} Dashboard API object
		 *
		 */
		create(options = {}) {
			let env, boardLoaderHelper;
			return this._glassContext.getSvc('.DashboardRuntime').then((runtimeService) => {
				const runtimeOptions = {
					featuresOverride: this._featuresOverride,
					assetId: options.assetId,
					spec: options.spec,
					profiles: options.profiles,
					apiConsumerConfig: this._apiConsumerConfig
				};
				if(!runtimeOptions.assetId && !runtimeOptions.spec) {
					runtimeOptions.spec = runtimeService.getDefaultSpec();
				}
				return runtimeService.getRuntimeEnvironment(runtimeOptions);
			}).then((environment) => {
				env = environment;
				boardLoaderHelper = new BoardLoaderHelper(env.dashboardApi);
				return boardLoaderHelper.loadLayoutExtensions(env.layoutExtensions);
			}).then(layoutExtensions => {
				boardLoaderHelper.registerLayoutExtensions(layoutExtensions);
				const legacyViewControllers = new LegacyViewControllers({
					environment: env
				});
				env.dashboardApi.getFeature('FeatureRegistry.internal').registerFeature('LegacyViewControllers', legacyViewControllers);
				return new DashboardCore({
					dashboardAPI: env.dashboardApi,
					internalDashboardAPI: env.internalDashboardAPI
				}).getAPI();
			}).then( (dashboardCore) => {
				const api = env.internalDashboardAPI;
				return api.onPageReady()
					.then( () => {
						return dashboardCore;
					});
			});
		}

		/**
		 *
		 * @param {String} featureName - Name of feature that is supported by the AppFactory, for example 'ContentStore'.
		 * @return {Object} Feature, for example ContentStore object.
		 */
		getFeature(featureName) {
			return this._featureMap.get(featureName);
		}
	}

	return AppFactory;
});